import { InvoiceEntry } from '@dataTypes/billing.types'
import {
  addPaymentEntry,
  fetchInvoiceEntries,
  fetchPatients,
} from '@store/actions/billing.action'
import { AppDispatch, RootState } from '@store/index'
import {
  selectInvoices,
  selectPatients,
  selectSelectedInvoices,
} from '@store/selectors/billing.selectors'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatInvoiceNumber } from '@utils/billing.utils'
import { logger } from '@utils/safeLogger'

const billingSources = [
  { label: 'All', value: 'all' },
  { label: 'Self', value: 'self' },
  { label: 'Family', value: 'family' },
]

interface UseAddInvoiceModalProps {
  isVisible: boolean
  prefill?: Partial<InvoiceEntry>
  closeModal: () => void
}

interface UseAddInvoiceModalReturn {
  source: string
  patient: string
  invoiceNum: string
  loading: boolean
  setInvoiceNum: (value: string) => void
  filteredPatients: Array<{ name: string }>
  invoiceOptions: Array<{ label: string; value: string }>
  availableSources: Array<{ label: string; value: string }>
  handleSourceChange: (value: string) => void
  handlePatientChange: (value: string) => void
  handleAdd: () => Promise<void>
}

export const useAddInvoiceModal = ({
  isVisible,
  prefill,
  closeModal,
}: UseAddInvoiceModalProps): UseAddInvoiceModalReturn => {
  const dispatch = useDispatch<AppDispatch>()
  const invoices = useSelector(selectInvoices)
  const patients = useSelector(selectPatients)
  const selectedInvoices = useSelector(selectSelectedInvoices)
  const { selectedPatient } = useSelector((state: RootState) => state.patient)
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )

  // Form state
  const [source, setSource] = useState('')
  const [patient, setPatient] = useState('')
  const [invoiceNum, setInvoiceNum] = useState('')
  const [loading, setLoading] = useState(false)

  // Reset form function
  const resetForm = useCallback(() => {
    setSource('')
    setPatient('')
    setInvoiceNum('')
  }, [])

  // Initialize form from prefill
  useEffect(() => {
    if (prefill) {
      setPatient(prefill.patientName || '')
      setInvoiceNum(prefill.invoiceNo || '')
      setSource('all')
    } else {
      setSource('')
      setPatient('')
      setInvoiceNum('')
    }
  }, [prefill, isVisible, selectedClinic?.clinic_id])

  // Fetch patients when modal opens or clinic changes
  useEffect(() => {
    if (!isVisible) return

    const fetchData = async (): Promise<void> => {
      try {
        await dispatch(fetchPatients()).unwrap()
        // Invoices are now fetched when a patient is selected, not upfront
      } catch (error) {
        logger.error('Error fetching data for AddInvoiceModal:', error)
      }
    }

    fetchData()
  }, [isVisible, dispatch, selectedClinic?.clinic_id])

  // Computed: Filtered patients
  const filteredPatients = useMemo(() => {
    if (source === 'self') {
      if (selectedPatient) {
        const fullName = `${selectedPatient.first_name || ''} ${
          selectedPatient.last_name || ''
        }`.trim()
        return [{ name: fullName }]
      }
      return []
    }
    if (source === 'family') {
      if (selectedPatient?.family_members_info) {
        return selectedPatient.family_members_info.map(
          (member: {
            patient_member_info?: {
              first_name?: string
              last_name?: string
            }
          }) => {
            const firstName = member.patient_member_info?.first_name || ''
            const lastName = member.patient_member_info?.last_name || ''
            const fullName = `${firstName} ${lastName}`.trim()
            return { name: fullName }
          },
        )
      }
      return []
    }
    return patients.map((p) => ({
      name: p.patientName,
    }))
  }, [source, patients, selectedPatient])

  // Computed: Invoice options
  const invoiceOptions = useMemo(() => {
    // Get list of invoice numbers that are already in selectedInvoices
    const selectedInvoiceNos = new Set(
      selectedInvoices.map((inv) => inv.invoiceNo),
    )

    return invoices
      .filter(
        (inv) =>
          inv.patientName === patient &&
          inv.patientResponsibilityBalance !== 0 &&
          !selectedInvoiceNos.has(inv.invoiceNo), // Filter out already selected invoices
      )
      .map((inv) => ({
        label: formatInvoiceNumber(inv.invoiceNo),
        value: inv.invoiceNo,
      }))
  }, [invoices, patient, selectedInvoices])

  // Computed: Available sources
  const availableSources = useMemo(() => {
    if (!selectedPatient?.patient_id) {
      return billingSources.filter((s) => s.value === 'all')
    }
    return billingSources
  }, [selectedPatient?.patient_id])

  // Event handlers
  const handleSourceChange = useCallback(
    async (value: string) => {
      setSource(value)
      if (value === 'self') {
        if (selectedPatient) {
          const fullName = `${selectedPatient.first_name || ''} ${
            selectedPatient.last_name || ''
          }`.trim()
          setPatient(fullName)
          // Fetch invoices for 'self' patient immediately
          if (selectedPatient.patient_id) {
            try {
              setLoading(true)
              await dispatch(
                fetchInvoiceEntries({ patient_id: selectedPatient.patient_id }),
              ).unwrap()
            } catch (error) {
              logger.error('Error fetching invoices for self:', error)
            } finally {
              setLoading(false)
            }
          }
        }
        setInvoiceNum('')
      } else if (value === '') {
        setPatient('')
        setInvoiceNum('')
      } else {
        setPatient('')
        setInvoiceNum('')
      }
    },
    [selectedPatient, dispatch],
  )

  const handlePatientChange = useCallback(
    async (value: string) => {
      setPatient(value)
      setInvoiceNum('')

      // Find patient by name to get patientId
      let patientId: string | undefined

      if (source === 'self' && selectedPatient?.patient_id) {
        // For 'self', use selectedPatient's ID
        patientId = selectedPatient.patient_id
      } else if (source === 'family' && selectedPatient?.family_members_info) {
        // For 'family', find the family member's ID
        const fullName = value.trim()
        const familyMember = selectedPatient.family_members_info.find(
          (member: {
            patient_member_info?: {
              first_name?: string
              last_name?: string
              patient_id?: string
            }
          }) => {
            const firstName = member.patient_member_info?.first_name || ''
            const lastName = member.patient_member_info?.last_name || ''
            const memberFullName = `${firstName} ${lastName}`.trim()
            return memberFullName === fullName
          },
        )
        patientId = familyMember?.patient_member_info?.patient_id
      } else {
        // For 'all', find patient in patients array
        const foundPatient = patients.find((p) => p.patientName === value)
        patientId = foundPatient?.patientId
      }

      // Fetch invoices for the selected patient
      if (patientId && value) {
        try {
          setLoading(true)
          await dispatch(
            fetchInvoiceEntries({ patient_id: patientId }),
          ).unwrap()
        } catch (error) {
          logger.error('Error fetching invoices for patient:', error)
        } finally {
          setLoading(false)
        }
      }
    },
    [source, selectedPatient, patients, dispatch],
  )

  const handleAdd = useCallback(async () => {
    if (!source || !patient || !invoiceNum) return

    const matchedInvoice = invoices.find(
      (inv: InvoiceEntry) =>
        inv.invoiceNo === invoiceNum && inv.patientName === patient,
    )

    if (!matchedInvoice) {
      console.error('Invoice not found')
      return
    }

    setLoading(true)
    try {
      await dispatch(addPaymentEntry(matchedInvoice)).unwrap()
      closeModal()
      resetForm()
    } catch (error) {
      logger.error('Failed to add invoice:', error)
    } finally {
      setLoading(false)
    }
  }, [source, patient, invoiceNum, invoices, dispatch, closeModal, resetForm])

  return {
    // Form state
    source,
    patient,
    invoiceNum,
    loading,
    setInvoiceNum,
    // Computed options
    filteredPatients,
    invoiceOptions,
    availableSources,
    // Event handlers
    handleSourceChange,
    handlePatientChange,
    handleAdd,
  }
}
